import { Pipe, PipeTransform, OnDestroy } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { I18nService } from 'i18n';
import { Subscription } from 'rxjs';
import { Logger } from '../services/logger.service';

/**
 * Locale-aware money formatting pipe that renders an amount together with its
 * currency code/symbol. Reacts to language changes like {@link LocaleNumberPipe}.
 *
 * Usage: {{ amount | currencyAmount: currencyCode }}
 *        {{ amount | currencyAmount: currencyCode : '1.2-2' }}
 *
 * Examples:
 * - en, USDT: $1,234.56 (USDT is displayed as USD)
 * - de, USDT: $1.234,56
 * - en, USD:  $1,234.56
 */
@Pipe({
  name: 'currencyAmount',
  standalone: true,
  pure: false // Required to react to language changes
})
export class CurrencyAmountPipe implements PipeTransform, OnDestroy {
  protected logger = new Logger('CurrencyAmountPipe');
  private decimalPipe?: DecimalPipe;
  private currentLocale = 'en-US';
  private langSubscription?: Subscription;

  // Currencies rendered with a leading symbol; everything else uses a trailing code.
  private static readonly SYMBOLS: Record<string, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£'
  };

  constructor(private i18nService: I18nService) {
    this.langSubscription = this.i18nService.currentLanguage$.subscribe(lang => this.updateLocale(lang));
    this.updateLocale(this.i18nService.getLanguage());
  }

  ngOnDestroy(): void {
    this.langSubscription?.unsubscribe();
  }

  private updateLocale(lang: string): void {
    const localeMap: Record<string, string> = {
      en: 'en-US', de: 'de-DE', es: 'es-ES', fr: 'fr-FR', pt: 'pt-PT', it: 'it-IT'
    };
    const langCode = (lang || 'en').split('-')[0];
    this.currentLocale = localeMap[langCode] || 'en-US';
    this.decimalPipe = new DecimalPipe(this.currentLocale);
  }

  transform(
    value: number | string | null | undefined,
    currency: string = 'USDT',
    digitsInfo: string = '1.2-2'
  ): string | null {
    if (value == null || value === '') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) {
      return null;
    }

    let formatted: string;
    try {
      formatted = this.decimalPipe?.transform(numValue, digitsInfo) ?? String(numValue);
    } catch (error) {
      this.logger.error('CurrencyAmountPipe error:', error);
      formatted = String(numValue);
    }

    // USDT amounts are displayed as USD (1:1)
    const rawCode = (currency || 'USDT').toUpperCase();
    const code = rawCode === 'USDT' ? 'USD' : rawCode;
    const symbol = CurrencyAmountPipe.SYMBOLS[code];
    return symbol ? `${symbol}${formatted}` : `${formatted} ${code}`;
  }
}
